/*
 * A loading indicator that grows instead of spinning blankly.
 *
 * <OrganicLoader
 *   label="Loading..."          the status text (pass the translated
 *                               organic:loading string). The live region is
 *                               mounted empty and the text put in right after,
 *                               because a live region that arrives already
 *                               filled is often not announced at all.
 *   slowLabel="Still working..."  replaces label after slowAfterMs (pass the
 *                               translated organic:stillWorking string)
 *   variant="auto"              'auto' | 'tip' | 'colony'; auto = 'tip' when
 *                               inline or size <= 24, else 'colony'
 *   size                        px; tip 18, colony 48 by default
 *   inline                      a <span> in running text instead of a <div>
 *   block                       centred column with vertical padding
 *   showLabel                   default: visible for 'colony', screen-reader
 *                               only for 'tip'; always visible when still
 *   announce={true}             role="status" aria-live="polite"; pass false
 *                               for secondary loaders on the same screen
 *   decorative={false}          only the aria-hidden art: no role, label, timer
 *   delayMs={200}               the art fades in after this (the label does not
 *                               wait for it)
 *   slowAfterMs={8000}          0 disables the slow label
 *   motif                       default: the current section's, from the URL
 * />
 *
 * Works outside a Router: the motif is read from window.location, never from a
 * router hook. Strings are props, not t() calls, so this library carries no
 * catalogue of its own; English defaults exist only as a last resort.
 */
import { useEffect, useRef, useState } from 'react';
import './organic.css';
import { useMotionGate } from './motion.js';
import { sectionMotif, LOADER_ART } from './sectionMotif.js';
import { miniColony } from './miniBranch.js';
import { hashString, makeRng } from './prng.js';
import { growStyle } from './artUtil.js';

export const DEFAULT_LOADING_LABEL = 'Loading…';
export const DEFAULT_SLOW_LABEL = 'Still working…';

const VIEW_BOX = { tip: '0 0 24 24', colony: '0 0 64 64' };

function TipArt() {
  return (
    <>
      <circle cx="12" cy="12" r="8.5" className="og-s-hypha" fill="none" strokeWidth="1.5" strokeOpacity=".35" />
      <g style={{ transformBox: 'view-box', transformOrigin: '12px 12px', animation: 'og-spin 1.1s linear infinite' }}>
        <path d="M12 3.5A8.5 8.5 0 0 1 20.08 9.37" className="og-line og-s-strong" strokeWidth="1.6" />
        <path d="M14.91 4.01l2-1.68" className="og-line og-s-strong" strokeWidth=".9" />
        <path d="M13.48 3.63l.9-2.3" className="og-line og-s-hypha" strokeWidth=".9"
          style={{ animation: 'og-bud 1.1s linear infinite' }} />
        <circle cx="20.08" cy="9.37" r="1.7" className="og-f-tip" />
      </g>
    </>
  );
}

const LOOP = 'og-loopfade 2.4s linear infinite';

/* A mini colony that grows the way a real one does: each leader from the
 * inoculum outward, each lateral once the leader's tip has passed its branch
 * point, the tips showing as the leaders finish. Only the dash moves; the
 * drawing is never scaled, so the inner hyphae stay where they grew. */
function GrowingColony({ mc, tips = false }) {
  const keyframes = (p) => (p.from === 0 ? 'og-grow-loop' : p.from < 0.5 ? 'og-grow-lat1' : 'og-grow-lat2');
  return (
    <g style={{ animation: LOOP }}>
      {mc.paths.map((p, i) => (
        <path key={i} d={p.d} className="og-line og-s-strong" strokeWidth={p.w}
          style={{ ...growStyle(p.len, 2400), animation: `${keyframes(p)} 2.4s linear infinite` }} />
      ))}
      {tips && mc.tips.map((t, i) => (
        <circle key={`t${i}`} cx={t.x} cy={t.y} r="1.4" className="og-f-tip"
          style={{ animation: 'og-show-late 2.4s linear infinite' }} />
      ))}
    </g>
  );
}

function AgarArt() {
  const mc = miniColony(hashString('loader:agar'), { cx: 32, cy: 32, n: 7, radius: 22 });
  return (
    <>
      <circle cx="32" cy="32" r="28" className="og-f-agar og-s-glass" strokeWidth="1.5" />
      <GrowingColony mc={mc} tips />
      <circle cx="32" cy="32" r="3" className="og-f-caplight" />
    </>
  );
}

function SoilArt() {
  const mc = miniColony(hashString('loader:soil'), { cx: 22, cy: 50, n: 5, radius: 16, yMin: 39 });
  return (
    <>
      <path d="M4 36Q20 33 32 36T60 35V60H4Z" className="og-f-hypha" opacity=".18" />
      <path d="M4 36Q20 33 32 36T60 35" className="og-line og-s-strong" strokeWidth="1" />
      <GrowingColony mc={mc} />
      <g style={{ animation: LOOP }}>
        <rect x="40.5" y="24" width="3" height="12" rx="1.5" className="og-f-stipe og-s-strong" strokeWidth=".6"
          style={{ transformBox: 'view-box', transformOrigin: '42px 36px', animation: 'og-rise-loop 2.4s var(--og-ease-emerge) infinite' }} />
        <g style={{ transformBox: 'view-box', transformOrigin: '42px 25px', animation: 'og-cap-loop 2.4s var(--og-ease-expand) infinite' }}>
          <path d="M35 25Q35 17 42 16Q49 17 49 25Z" className="og-f-cap" />
          <path d="M36 25H48" className="og-line og-s-gill" strokeWidth=".8" />
        </g>
      </g>
    </>
  );
}

const NUCLEI = [[12, 'og-f-caplight'], [16, 'og-f-gill'], [28, 'og-f-caplight'], [32, 'og-f-gill'],
  [44, 'og-f-caplight'], [48, 'og-f-gill']];

function GeneticArt({ rtl }) {
  const wall = { ...growStyle(120, 2400), animation: 'og-grow-loop 2.4s linear infinite' };
  return (
    <g transform={rtl ? 'matrix(-1 0 0 1 64 0)' : undefined} data-og-mirror={rtl ? '' : undefined}>
      <rect x="6" y="28" width="48" height="8" className="og-f-agar" opacity=".6" />
      <path d="M22 28V36M38 28V36" className="og-line og-s-strong" strokeWidth="1" />
      <path d="M27 27C26 22 19 22 18 27M43 27C42 22 35 22 34 27" className="og-line og-s-hypha" strokeWidth=".8" />
      {/* The wall that grows and the nuclei that migrate fade out before the
          loop starts again, so neither is seen jumping back. */}
      <g style={{ animation: LOOP }} data-og-loop="">
        <path d="M6 28H54Q60 28 60 32Q60 36 54 36H6" className="og-line og-s-strong" strokeWidth="1.2" style={wall} />
        <g style={{ animation: 'og-nuclei-loop 2.4s ease-in-out infinite' }}>
          {NUCLEI.map(([x, cls]) => <ellipse key={x} cx={x} cy="32" rx="2.6" ry="1.8" className={cls} />)}
        </g>
      </g>
      <circle cx="60" cy="32" r="1.6" className="og-f-tip" style={{ animation: 'og-node 2.4s infinite' }} />
    </g>
  );
}

function networkGeometry() {
  const rng = makeRng(hashString('loader:network'));
  const nodes = [];
  for (let i = 0; i < 6; i++) {
    const a = ((30 + 60 * i) * Math.PI) / 180;
    nodes.push([32 + 20 * Math.cos(a), 32 + 20 * Math.sin(a)]);
  }
  const r1 = (n) => Math.round(n * 10) / 10;
  const spokes = nodes.map(([x, y]) => {
    const mx = (32 + x) / 2;
    const my = (32 + y) / 2;
    const len = Math.hypot(x - 32, y - 32);
    const off = rng.range(-4, 4);
    return `M32 32Q${r1(mx - ((y - 32) / len) * off)} ${r1(my + ((x - 32) / len) * off)} ${r1(x)} ${r1(y)}`;
  });
  const rings = [[0, 1], [2, 3], [4, 5]].map(([a, b]) => `M${r1(nodes[a][0])} ${r1(nodes[a][1])}L${r1(nodes[b][0])} ${r1(nodes[b][1])}`);
  return { nodes: nodes.map(([x, y]) => [r1(x), r1(y)]), spokes, rings };
}

let network = null;

function NetworkArt() {
  if (!network) network = networkGeometry();
  return (
    <>
      {network.spokes.map((d, i) => <path key={i} d={d} className="og-line og-s-hypha" strokeWidth="1" />)}
      {network.rings.map((d, i) => <path key={`r${i}`} d={d} className="og-line og-s-hypha" strokeWidth=".8" />)}
      <circle cx="32" cy="32" r="2.2" className="og-f-strong" />
      {network.nodes.map(([x, y], i) => <circle key={`n${i}`} cx={x} cy={y} r="2.2" className="og-f-strong" />)}
      {network.nodes.map(([x, y], i) => (
        <circle key={`o${i}`} cx={x} cy={y} r="2.6" className="og-f-tip" opacity=".35"
          style={{ animation: `og-node 1.8s ${i * 0.25}s infinite` }} />
      ))}
    </>
  );
}

const COLONY_ART = { agar: AgarArt, soil: SoilArt, genetic: GeneticArt, network: NetworkArt };

export default function OrganicLoader({
  label = DEFAULT_LOADING_LABEL, slowLabel = DEFAULT_SLOW_LABEL, variant = 'auto', size, inline = false,
  block = false, showLabel, announce = true, decorative = false, delayMs = 200, slowAfterMs = 8000, motif,
}) {
  const rootRef = useRef(null);
  const gate = useMotionGate(rootRef);
  const [slow, setSlow] = useState(false);
  // False on the first render: see `label` above.
  const [spoken, setSpoken] = useState(false);
  useEffect(() => { setSpoken(true); }, []);

  useEffect(() => {
    if (decorative || !(slowAfterMs > 0)) return undefined;
    const id = setTimeout(() => setSlow(true), slowAfterMs);
    return () => clearTimeout(id);
  }, [decorative, slowAfterMs]);

  const kind = variant === 'tip' || variant === 'colony' ? variant
    : inline || (size && size <= 24) ? 'tip' : 'colony';
  const px = size || (kind === 'tip' ? 18 : 48);
  const m = motif || sectionMotif(typeof window !== 'undefined' ? window.location.pathname : '/');
  const artName = Object.prototype.hasOwnProperty.call(LOADER_ART, m) ? LOADER_ART[m] : 'network';
  const Art = kind === 'tip' ? TipArt : COLONY_ART[artName];

  const art = (
    <svg className="og-loader-art" aria-hidden="true" focusable="false" width={px} height={px}
      viewBox={VIEW_BOX[kind]} style={{ '--og-delay': `${delayMs}ms`, flex: '0 0 auto', display: 'block' }}>
      <Art rtl={gate.dir === 'rtl'} />
    </svg>
  );

  const gateAttrs = {
    'data-og-paused': gate.paused ? '' : undefined,
    'data-og-still': gate.still ? '' : undefined,
  };

  if (decorative) {
    return (
      <span ref={rootRef} className="og-loader og-anim" aria-hidden="true" data-variant={kind} data-motif={m}
        style={{ display: 'inline-flex', verticalAlign: 'middle' }} {...gateAttrs}>
        {art}
      </span>
    );
  }

  const Tag = inline ? 'span' : 'div';
  const visible = gate.still || (showLabel ?? kind === 'colony');
  const live = announce ? { role: 'status', 'aria-live': 'polite' } : {};
  return (
    <Tag ref={rootRef} className="og-loader og-anim" data-variant={kind} data-motif={m} {...live} {...gateAttrs}
      style={{
        display: inline ? 'inline-flex' : 'flex',
        alignItems: 'center',
        gap: 8,
        flexDirection: block ? 'column' : 'row',
        justifyContent: block ? 'center' : 'flex-start',
        padding: block ? '24px 0' : 0,
        color: 'var(--c-text-muted-fg)',
        fontSize: inline ? 'inherit' : 14,
        position: 'relative',
      }}>
      {art}
      <span className={visible ? undefined : 'og-sr'}>{announce && !spoken ? '' : (slow ? slowLabel : label)}</span>
    </Tag>
  );
}
